import { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Helmet } from 'react-helmet-async';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  Warehouse, 
  Package, 
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Trash2,
  BarChart3,
  Search,
  Filter,
  X
} from 'lucide-react';

// Warehouse to State mapping - comprehensive mapping based on Amazon FC codes
// This mapping will be used for state aggregation
const WAREHOUSE_STATE_MAP = {
  // Delhi/NCR
  'DEL': 'Delhi', 'DED': 'Delhi', 'DEX': 'Delhi', 'FDLA': 'Delhi', 'FDLB': 'Delhi', 'FDLC': 'Delhi',
  // Maharashtra (Mumbai/Pune)
  'BOM': 'Maharashtra', 'PNQ': 'Maharashtra', 'FBOB': 'Maharashtra', 'FBOC': 'Maharashtra', 
  'FBOD': 'Maharashtra', 'FBOF': 'Maharashtra', 'FBOA': 'Maharashtra', 'FBOI': 'Maharashtra',
  'FPNA': 'Maharashtra', 'FPNB': 'Maharashtra', 'NAX': 'Maharashtra',
  // Karnataka (Bangalore)
  'BLR': 'Bangalore', 'BLX': 'Bangalore', 'FBLJ': 'Bangalore', 'FBLF': 'Bangalore', 'FBLL': 'Bangalore',
  'SBLL': 'Bangalore', 'SBLY': 'Bangalore', 'SBLZ': 'Bangalore',
  // West Bengal (Kolkata)
  'CCX': 'Kolkata', 'FCCB': 'Kolkata',
  // Telangana (Hyderabad)
  'HYD': 'Telangana', 'FHYE': 'Telangana', 'SHYH': 'Telangana', 'SHYB': 'Telangana',
  // Tamil Nadu (Chennai)
  'MAA': 'Tamil Nadu', 'CJB': 'Tamil Nadu', 'SMAB': 'Tamil Nadu', 'SCJF': 'Tamil Nadu', 'FCJD': 'Tamil Nadu',
  // Gujarat (Ahmedabad)
  'AMD': 'Gujarat', 'FAMA': 'Gujarat', 'GAX': 'Gujarat', 'SGAC': 'Gujarat',
  // Haryana
  'ISK': 'Haryana', 'FISA': 'Haryana', 'FIXI': 'Haryana', 'FIXF': 'Haryana',
  // Rajasthan (Jaipur)
  'JAI': 'Rajasthan', 'JPX': 'Rajasthan', 'FJAA': 'Rajasthan',
  // Uttar Pradesh (Lucknow/others)
  'LKO': 'UP', 'LDX': 'UP', 'SLKD': 'UP', 'FLKA': 'UP',
  // Punjab
  'ATX': 'Punjab', 'SATB': 'Punjab', 'FSTA': 'Punjab', 'PAX': 'Punjab',
  // Madhya Pradesh
  'IDX': 'MP', 'FIDA': 'MP',
  // Bihar
  'PAT': 'Bihar', 'SPAB': 'Bihar',
  // Assam
  'XNRW': 'Assam',
  // Kerala
  'COA': 'Kerala', 'FCOA': 'Kerala', 'FKNB': 'Kerala',
  // Andhra Pradesh
  'VTZ': 'Andhra Pradesh', 'FVTG': 'Andhra Pradesh', 'FVTH': 'Andhra Pradesh',
  // Odisha
  'BBI': 'Odisha', 'FBBA': 'Odisha',
  // Others - will be grouped dynamically
  'SAME': 'Delhi', 'SDEG': 'Delhi', 'SDEB': 'Delhi',
  'XSAJ': 'Rajasthan', 'XSAD': 'Rajasthan', 'XSIP': 'Rajasthan', 'XSIR': 'Rajasthan',
  'SBOB': 'Maharashtra', 'SBHF': 'Maharashtra',
  'XWAA': 'Assam', 'XECP': 'Kolkata',
  'FMAB': 'Maharashtra', 'FMAC': 'Maharashtra', 'FMYA': 'Maharashtra', 'FMZA': 'Maharashtra',
  'FBHB': 'Telangana', 'FTRA': 'Telangana', 'FNAA': 'Maharashtra',
  'YNGD': 'Delhi', 'YNGG': 'Gujarat', 'SCCE': 'Kolkata', 'FCJA': 'Tamil Nadu',
};

// Helper function to get state from warehouse code
const getStateFromWarehouse = (warehouseCode) => {
  if (!warehouseCode) return 'Other';
  const code = warehouseCode.toString().toUpperCase().trim();
  
  // Direct match
  if (WAREHOUSE_STATE_MAP[code]) return WAREHOUSE_STATE_MAP[code];
  
  // Check prefix matches (3 chars, then 2 chars)
  const prefix3 = code.substring(0, 3);
  if (WAREHOUSE_STATE_MAP[prefix3]) return WAREHOUSE_STATE_MAP[prefix3];
  
  const prefix2 = code.substring(0, 2);
  // Common 2-char prefixes
  const twoCharMap = {
    'DE': 'Delhi', 'BL': 'Bangalore', 'BO': 'Maharashtra', 'CC': 'Kolkata',
    'HY': 'Telangana', 'MA': 'Tamil Nadu', 'AM': 'Gujarat', 'IS': 'Haryana',
    'JP': 'Rajasthan', 'LK': 'UP', 'AT': 'Punjab', 'PA': 'Bihar'
  };
  if (twoCharMap[prefix2]) return twoCharMap[prefix2];
  
  return 'Other';
};

const InventoryWarehouseTool = () => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedData, setProcessedData] = useState(null);
  const [error, setError] = useState(null);
  
  // Filter states
  const [titleFilter, setTitleFilter] = useState('');
  const [skuFilter, setSkuFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const processExcelFile = useCallback((file) => {
    setIsProcessing(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        if (jsonData.length < 2) {
          throw new Error('Excel file appears to be empty or has no data rows');
        }

        // Get headers from first row
        const headers = jsonData[0];
        
        // Find column indexes
        const asinIndex = headers.findIndex(h => 
          h && h.toString().toLowerCase() === 'asin'
        );
        const locationIndex = headers.findIndex(h => 
          h && h.toString().toLowerCase() === 'location'
        );
        const endingBalanceIndex = headers.findIndex(h => 
          h && h.toString().toLowerCase().includes('ending warehouse balance')
        );
        const titleIndex = headers.findIndex(h => 
          h && h.toString().toLowerCase() === 'title'
        );
        const mskuIndex = headers.findIndex(h => 
          h && h.toString().toLowerCase() === 'msku'
        );
        const fnskuIndex = headers.findIndex(h => 
          h && h.toString().toLowerCase() === 'fnsku'
        );

        // Validate required columns
        if (asinIndex === -1) {
          throw new Error('Could not find ASIN column in the Excel file');
        }
        if (locationIndex === -1) {
          throw new Error('Could not find Location column in the Excel file');
        }
        if (endingBalanceIndex === -1) {
          throw new Error('Could not find "Ending Warehouse Balance" column in the Excel file');
        }

        // Process data rows (skip header)
        const dataRows = jsonData.slice(1).filter(row => {
          return row && row.length > 0 && row[asinIndex];
        });

        // Collect all unique warehouse locations dynamically
        const allLocations = new Set();
        dataRows.forEach(row => {
          const location = row[locationIndex];
          if (location && location.toString().trim()) {
            allLocations.add(location.toString().trim());
          }
        });
        const sortedLocations = Array.from(allLocations).sort();

        // Determine states present in data
        const statesInData = new Set();
        sortedLocations.forEach(loc => {
          statesInData.add(getStateFromWarehouse(loc));
        });
        const sortedStates = Array.from(statesInData).filter(s => s !== 'Other').sort();
        if (statesInData.has('Other')) sortedStates.push('Other');

        // Group by ASIN and sum quantities per warehouse
        const asinGroups = {};

        dataRows.forEach(row => {
          const asin = row[asinIndex]?.toString().trim();
          const location = row[locationIndex]?.toString().trim();
          const quantity = parseInt(row[endingBalanceIndex]) || 0;

          if (!asin || !location) return;

          if (!asinGroups[asin]) {
            asinGroups[asin] = {
              asin: asin,
              title: row[titleIndex] || 'N/A',
              msku: row[mskuIndex] || 'N/A',
              fnsku: row[fnskuIndex] || 'N/A',
              warehouses: {},
              states: {},
              total: 0
            };
            // Initialize all warehouses to 0
            sortedLocations.forEach(loc => {
              asinGroups[asin].warehouses[loc] = 0;
            });
            // Initialize all states to 0
            sortedStates.forEach(state => {
              asinGroups[asin].states[state] = 0;
            });
          }

          // Add quantity to the specific warehouse
          asinGroups[asin].warehouses[location] += quantity;
          asinGroups[asin].total += quantity;
          
          // Add quantity to state aggregation
          const state = getStateFromWarehouse(location);
          if (asinGroups[asin].states[state] !== undefined) {
            asinGroups[asin].states[state] += quantity;
          }
        });

        // Convert to array and sort by ASIN
        const processedRows = Object.values(asinGroups).sort((a, b) => 
          a.asin.localeCompare(b.asin)
        );

        // Calculate totals per warehouse
        const warehouseTotals = {};
        sortedLocations.forEach(loc => {
          warehouseTotals[loc] = processedRows.reduce((sum, row) => sum + row.warehouses[loc], 0);
        });
        
        // Calculate totals per state
        const stateTotals = {};
        sortedStates.forEach(state => {
          stateTotals[state] = processedRows.reduce((sum, row) => sum + (row.states[state] || 0), 0);
        });
        
        const grandTotal = processedRows.reduce((sum, row) => sum + row.total, 0);

        setProcessedData({
          rows: processedRows,
          warehouses: sortedLocations,
          states: sortedStates,
          warehouseTotals: warehouseTotals,
          stateTotals: stateTotals,
          grandTotal: grandTotal,
          totalASINs: processedRows.length
        });
        
        setIsProcessing(false);
      } catch (err) {
        setError(err.message || 'Failed to process Excel file');
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      setError('Failed to read the file');
      setIsProcessing(false);
    };

    reader.readAsArrayBuffer(file);
  }, []);

  // Filtered data based on search
  const filteredData = useMemo(() => {
    if (!processedData) return null;
    
    let filtered = processedData.rows;
    
    if (titleFilter.trim()) {
      const searchTerm = titleFilter.toLowerCase().trim();
      filtered = filtered.filter(row => 
        row.title.toLowerCase().includes(searchTerm)
      );
    }
    
    if (skuFilter.trim()) {
      const searchTerm = skuFilter.toLowerCase().trim();
      filtered = filtered.filter(row => 
        row.msku.toLowerCase().includes(searchTerm) ||
        row.asin.toLowerCase().includes(searchTerm)
      );
    }
    
    return filtered;
  }, [processedData, titleFilter, skuFilter]);

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'
      ];
      
      if (!validTypes.includes(uploadedFile.type) && 
          !uploadedFile.name.endsWith('.xlsx') && 
          !uploadedFile.name.endsWith('.xls') &&
          !uploadedFile.name.endsWith('.csv')) {
        setError('Please upload a valid Excel file (.xlsx, .xls) or CSV file');
        return;
      }

      setFile(uploadedFile);
      setTitleFilter('');
      setSkuFilter('');
      processExcelFile(uploadedFile);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setTitleFilter('');
      setSkuFilter('');
      processExcelFile(droppedFile);
    }
  }, [processExcelFile]);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const clearFilters = () => {
    setTitleFilter('');
    setSkuFilter('');
  };

  const downloadReport = () => {
    if (!processedData) return;

    // Create workbook
    const wb = XLSX.utils.book_new();

    // Build header row: ASIN, Title, MSKU, FNSKU, [Warehouses...], [States...], Total
    const headerRow = [
      'ASIN', 'Title', 'MSKU', 'FNSKU', 
      ...processedData.warehouses, 
      ...processedData.states,
      'Total'
    ];
    
    // Use filtered data if filters are active
    const dataToExport = filteredData || processedData.rows;
    
    // Build data rows
    const dataRows = dataToExport.map(row => {
      const warehouseValues = processedData.warehouses.map(wh => row.warehouses[wh] || 0);
      const stateValues = processedData.states.map(st => row.states[st] || 0);
      return [row.asin, row.title, row.msku, row.fnsku, ...warehouseValues, ...stateValues, row.total];
    });

    // Add totals row
    const filteredWarehouseTotals = processedData.warehouses.map(wh => 
      dataToExport.reduce((sum, row) => sum + (row.warehouses[wh] || 0), 0)
    );
    const filteredStateTotals = processedData.states.map(st => 
      dataToExport.reduce((sum, row) => sum + (row.states[st] || 0), 0)
    );
    const filteredGrandTotal = dataToExport.reduce((sum, row) => sum + row.total, 0);
    
    const totalsRow = [
      'TOTAL', '', '', '', 
      ...filteredWarehouseTotals,
      ...filteredStateTotals,
      filteredGrandTotal
    ];

    // Combine all rows
    const sheetData = [
      ['Amazon FBA Inventory - Warehouse Report'],
      ['Generated on:', new Date().toLocaleString()],
      ['Total ASINs:', dataToExport.length],
      ['Total Warehouses:', processedData.warehouses.length],
      ['Total States:', processedData.states.length],
      titleFilter ? ['Title Filter:', titleFilter] : [],
      skuFilter ? ['SKU Filter:', skuFilter] : [],
      [],
      headerRow,
      ...dataRows,
      [],
      totalsRow
    ].filter(row => row.length > 0);

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    
    // Set column widths
    const colWidths = [
      { wch: 15 }, // ASIN
      { wch: 40 }, // Title
      { wch: 20 }, // MSKU
      { wch: 15 }, // FNSKU
      ...processedData.warehouses.map(() => ({ wch: 8 })), // Warehouses
      ...processedData.states.map(() => ({ wch: 12 })), // States
      { wch: 10 }  // Total
    ];
    worksheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, worksheet, 'Warehouse Inventory');

    // Generate and download
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const filterSuffix = (titleFilter || skuFilter) ? '_filtered' : '';
    saveAs(blob, `Warehouse_Inventory_Report${filterSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const resetTool = () => {
    setFile(null);
    setProcessedData(null);
    setError(null);
    setTitleFilter('');
    setSkuFilter('');
  };

  const hasActiveFilters = titleFilter.trim() || skuFilter.trim();

  return (
    <div className="min-h-screen pt-32 pb-16">
      <Helmet>
        <title>Free E-commerce Tools for Sellers | Obiquiste Solutions</title>
        <meta name="description" content="Free tools for Amazon & Flipkart sellers. Inventory reconciliation, sales calculator & more. Boost your e-commerce business with our free seller tools." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/tools/inventory-warehouse" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-100 py-4 border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-slate-700 hover:text-primary font-medium">Home</Link>
            <ChevronRight size={16} className="text-slate-400" />
            <span className="text-primary font-semibold">Inventory Warehouse Tool</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Warehouse className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            Amazon FBA Inventory <span className="text-primary">Reconcile Tool</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Upload your Amazon Daily Inventory Report (DDS) and instantly see your stock organized by ASIN with warehouse-wise and state-wise quantities. Filter by Title or SKU for quick lookup.
          </p>
        </motion.div>

        {/* How it works */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: Upload, title: '1. Upload Excel', desc: 'Upload your Amazon DDS report' },
            { icon: BarChart3, title: '2. Auto Process', desc: 'Groups by ASIN & warehouse' },
            { icon: Filter, title: '3. Filter Results', desc: 'Search by Title or SKU' },
            { icon: Download, title: '4. Download Report', desc: 'Get reconciled inventory' }
          ].map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-xl p-5 border border-slate-200 text-center"
            >
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <step.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1 text-sm">{step.title}</h3>
              <p className="text-xs text-slate-600">{step.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Upload Section */}
        {!processedData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto"
          >
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all ${
                isProcessing 
                  ? 'border-primary bg-primary/5' 
                  : error 
                    ? 'border-red-400 bg-red-50' 
                    : 'border-slate-300 hover:border-primary hover:bg-slate-50'
              }`}
            >
              {isProcessing ? (
                <div className="space-y-4">
                  <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                  <p className="text-primary font-medium">Processing your file...</p>
                </div>
              ) : error ? (
                <div className="space-y-4">
                  <AlertCircle className="w-16 h-16 text-red-500 mx-auto" />
                  <p className="text-red-600 font-medium">{error}</p>
                  <Button onClick={resetTool} variant="outline" className="mt-4">
                    Try Again
                  </Button>
                </div>
              ) : (
                <>
                  <FileSpreadsheet className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                    Drop your Amazon FBA DDS report here
                  </h3>
                  <p className="text-slate-600 mb-6">
                    or click to browse from your computer
                  </p>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload-input"
                    data-testid="file-upload-input"
                  />
                  <label 
                    htmlFor="file-upload-input"
                    className="inline-flex items-center justify-center cursor-pointer bg-primary text-white hover:bg-primary-600 px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Select Excel File
                  </label>
                  <p className="text-xs text-slate-500 mt-4">
                    Supports .xlsx, .xls, and .csv files
                  </p>
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Results Section */}
        {processedData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                <Package className="w-6 h-6 mb-2 opacity-80" />
                <p className="text-2xl font-bold">{processedData.totalASINs.toLocaleString()}</p>
                <p className="text-blue-100 text-sm">Total ASINs</p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
                <Warehouse className="w-6 h-6 mb-2 opacity-80" />
                <p className="text-2xl font-bold">{processedData.warehouses.length}</p>
                <p className="text-green-100 text-sm">Warehouses</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
                <BarChart3 className="w-6 h-6 mb-2 opacity-80" />
                <p className="text-2xl font-bold">{processedData.states.length}</p>
                <p className="text-purple-100 text-sm">States</p>
              </div>
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
                <Package className="w-6 h-6 mb-2 opacity-80" />
                <p className="text-2xl font-bold">{processedData.grandTotal.toLocaleString()}</p>
                <p className="text-orange-100 text-sm">Total Units</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 flex flex-col justify-center">
                <Button 
                  onClick={downloadReport}
                  className="bg-primary text-white hover:bg-primary-600 text-sm mb-2"
                  data-testid="download-report-btn"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
                <Button 
                  onClick={resetTool}
                  variant="outline"
                  className="text-slate-600 text-sm"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  New File
                </Button>
              </div>
            </div>

            {/* File Info */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center space-x-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-green-800 text-sm">File processed successfully!</p>
                <p className="text-xs text-green-600 truncate">{file?.name}</p>
              </div>
            </div>

            {/* Filter Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Filter className="w-5 h-5 text-slate-600" />
                  <h3 className="font-semibold text-slate-900">Filter Results</h3>
                  {hasActiveFilters && (
                    <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                      {filteredData?.length || 0} results
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-slate-500 hover:text-slate-700"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="md:hidden"
                  >
                    {showFilters ? 'Hide' : 'Show'} Filters
                  </Button>
                </div>
              </div>
              
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${showFilters ? 'block' : 'hidden md:grid'}`}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search by Product Title..."
                    value={titleFilter}
                    onChange={(e) => setTitleFilter(e.target.value)}
                    className="pl-10"
                    data-testid="title-filter-input"
                  />
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search by SKU (MSKU) or ASIN..."
                    value={skuFilter}
                    onChange={(e) => setSkuFilter(e.target.value)}
                    className="pl-10"
                    data-testid="sku-filter-input"
                  />
                </div>
              </div>
            </div>

            {/* State-wise Summary */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h3 className="font-heading text-lg font-bold mb-3">State-wise Summary</h3>
              <div className="flex flex-wrap gap-2">
                {processedData.states.map((state, idx) => (
                  <div key={idx} className="bg-gradient-to-br from-slate-100 to-slate-50 rounded-lg px-3 py-2 text-center border border-slate-200">
                    <p className="font-semibold text-slate-900 text-sm">{state}</p>
                    <p className="text-xs text-slate-600">{processedData.stateTotals[state]?.toLocaleString() || 0} units</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Warehouse Summary - Collapsible on mobile */}
            <details className="bg-white rounded-xl border border-slate-200">
              <summary className="p-4 cursor-pointer font-heading text-lg font-bold flex items-center justify-between">
                <span>Warehouse Summary ({processedData.warehouses.length} FCs)</span>
                <ChevronRight className="w-5 h-5 text-slate-400 transform transition-transform details-open:rotate-90" />
              </summary>
              <div className="px-4 pb-4">
                <div className="flex flex-wrap gap-2">
                  {processedData.warehouses.map((wh, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg px-3 py-1.5 text-center border border-slate-100">
                      <p className="font-bold text-slate-900 text-xs">{wh}</p>
                      <p className="text-xs text-slate-500">{processedData.warehouseTotals[wh]?.toLocaleString() || 0}</p>
                    </div>
                  ))}
                </div>
              </div>
            </details>

            {/* Data Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-3 text-white">
                <h3 className="font-heading text-lg font-bold">ASIN-wise Inventory</h3>
                <p className="text-slate-300 text-xs">
                  {hasActiveFilters 
                    ? `Showing ${filteredData?.length || 0} of ${processedData.totalASINs} ASINs`
                    : `All ${processedData.totalASINs} ASINs`
                  }
                </p>
              </div>
              
              {filteredData && filteredData.length === 0 ? (
                <div className="p-12 text-center">
                  <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 font-medium">No products match your search</p>
                  <p className="text-slate-500 text-sm mt-1">Try adjusting your filters</p>
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="mt-4"
                  >
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3 font-semibold text-slate-700 border-b whitespace-nowrap">ASIN</th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-700 border-b whitespace-nowrap">Title</th>
                        <th className="text-left py-2 px-3 font-semibold text-slate-700 border-b whitespace-nowrap">MSKU</th>
                        {processedData.warehouses.map((wh, idx) => (
                          <th key={idx} className="text-center py-2 px-2 font-semibold text-slate-700 border-b bg-blue-50/50 whitespace-nowrap">
                            {wh}
                          </th>
                        ))}
                        {processedData.states.map((st, idx) => (
                          <th key={idx} className="text-center py-2 px-2 font-semibold text-slate-700 border-b bg-purple-50/50 whitespace-nowrap">
                            {st}
                          </th>
                        ))}
                        <th className="text-center py-2 px-3 font-semibold text-slate-700 border-b bg-green-50 whitespace-nowrap">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(filteredData || processedData.rows).slice(0, 50).map((row, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3 font-medium text-slate-900 whitespace-nowrap">{row.asin}</td>
                          <td className="py-2 px-3 text-slate-600 max-w-[200px] truncate" title={row.title}>
                            {row.title.length > 30 ? row.title.substring(0, 30) + '...' : row.title}
                          </td>
                          <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{row.msku}</td>
                          {processedData.warehouses.map((wh, whIdx) => (
                            <td key={whIdx} className="py-2 px-2 text-center text-slate-700">
                              {row.warehouses[wh] > 0 ? row.warehouses[wh] : '-'}
                            </td>
                          ))}
                          {processedData.states.map((st, stIdx) => (
                            <td key={stIdx} className="py-2 px-2 text-center text-purple-700 bg-purple-50/30">
                              {row.states[st] > 0 ? row.states[st] : '-'}
                            </td>
                          ))}
                          <td className="py-2 px-3 text-center font-bold text-green-600 bg-green-50/50">
                            {row.total}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-100">
                      <tr>
                        <td className="py-2 px-3 font-bold text-slate-900" colSpan={3}>TOTAL</td>
                        {processedData.warehouses.map((wh, idx) => {
                          const total = (filteredData || processedData.rows).reduce((sum, row) => sum + (row.warehouses[wh] || 0), 0);
                          return (
                            <td key={idx} className="py-2 px-2 text-center font-bold text-blue-600">
                              {total > 0 ? total.toLocaleString() : '-'}
                            </td>
                          );
                        })}
                        {processedData.states.map((st, idx) => {
                          const total = (filteredData || processedData.rows).reduce((sum, row) => sum + (row.states[st] || 0), 0);
                          return (
                            <td key={idx} className="py-2 px-2 text-center font-bold text-purple-600">
                              {total > 0 ? total.toLocaleString() : '-'}
                            </td>
                          );
                        })}
                        <td className="py-2 px-3 text-center font-bold text-green-600 bg-green-100">
                          {(filteredData || processedData.rows).reduce((sum, row) => sum + row.total, 0).toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              
              {filteredData && filteredData.length > 50 && (
                <div className="px-4 py-3 bg-slate-50 border-t">
                  <p className="text-xs text-slate-600">
                    Showing 50 of {filteredData.length} ASINs. Download the report to see all data.
                  </p>
                </div>
              )}
            </div>

            {/* Download CTA */}
            <div className="bg-gradient-to-r from-primary to-primary-600 rounded-2xl p-6 text-center text-white">
              <h3 className="font-heading text-xl font-bold mb-3">
                Download Complete Report
              </h3>
              <p className="text-primary-100 mb-4 text-sm">
                Get a detailed Excel report with all ASINs, warehouse-wise and state-wise inventory.
                {hasActiveFilters && ' (Filters will be applied)'}
              </p>
              <Button 
                onClick={downloadReport}
                className="bg-white text-primary hover:bg-slate-100 px-6 py-5 text-base font-semibold"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Excel Report
              </Button>
            </div>
          </motion.div>
        )}

        {/* How to Download Your Inventory Ledger Report Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16 bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden"
        >
          {/* Section Header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-8 text-white">
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
              How to Download Your Inventory Ledger Report
            </h2>
            <p className="text-slate-300 text-lg">
              Follow these steps in Amazon Seller Central:
            </p>
          </div>

          {/* Steps Container */}
          <div className="p-6 md:p-8">
            <div className="space-y-6">
              {/* Step 1 */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">Go to Reports</h3>
                  <p className="text-slate-600">
                    Login to Amazon Seller Central → Click <span className="font-semibold text-slate-800">"Reports"</span> in top menu
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">Select Fulfillment</h3>
                  <p className="text-slate-600">
                    Click on <span className="font-semibold text-slate-800">"Fulfillment"</span> from the dropdown
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
                  3
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">Open Inventory Ledger</h3>
                  <p className="text-slate-600">
                    In the left sidebar, under "Inventory" section → Click <span className="font-semibold text-slate-800">"Inventory Ledger"</span>
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
                  4
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">Set Download Options</h3>
                  <ul className="text-slate-600 mt-2 space-y-1">
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      Click <span className="font-semibold text-slate-800">"Download"</span> tab
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      Aggregate by location: Select <span className="font-semibold text-slate-800">"Fulfillment centre"</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      Aggregate by time: Select <span className="font-semibold text-slate-800">"Daily"</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary mr-2">•</span>
                      Date range: Select date from <span className="font-semibold text-slate-800">2 DAYS AGO</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg">
                  5
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-lg text-slate-900 mb-1">Download</h3>
                  <p className="text-slate-600">
                    Click <span className="font-semibold text-slate-800">"Request .csv Download"</span> button
                  </p>
                </div>
              </div>
            </div>

            {/* Pro Tip Box */}
            <div className="mt-8 bg-amber-50 border-l-4 border-amber-400 rounded-lg p-5">
              <div className="flex items-start">
                <span className="text-2xl mr-3">💡</span>
                <div>
                  <h4 className="font-bold text-amber-800 text-lg mb-1">Pro Tip</h4>
                  <p className="text-amber-900">
                    Always download the report for <span className="font-bold">2 days before today</span>.
                  </p>
                  <p className="text-amber-800 mt-1">
                    <strong>Example:</strong> If today is 29th March → Download for 27th March.
                  </p>
                  <p className="text-amber-700 mt-2 text-sm">
                    This ensures Amazon has updated all inventory movements for accurate results.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-8 text-center">
              <a 
                href="https://sellercentral.amazon.in/reportcentral/LEDGER_REPORT/1"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-[#FF9900] hover:bg-[#e88a00] text-white px-8 py-6 text-lg font-semibold rounded-full shadow-lg">
                  Open Inventory Ledger in Seller Central
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>

            {/* Screenshot */}
            <div className="mt-10">
              <div className="bg-slate-100 rounded-xl p-4 border border-slate-200">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ecom-growth-hub-4/artifacts/jqbfxn3n_Screenshot%202026-03-29%20at%2011.46.16%20AM.png"
                  alt="Amazon Seller Central - Inventory Ledger Report Page"
                  className="w-full rounded-lg shadow-md border border-slate-300"
                />
                <p className="text-center text-slate-600 text-sm mt-3 italic">
                  Amazon Seller Central - Inventory Ledger Report Page
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InventoryWarehouseTool;
